





































































































































import Vue from 'vue';
import { AccountModule } from '@/store/modules/account';
import * as Cache from '@/utils/cache.util';
import { downloadByAuthUrl } from '@/utils/file.util';
import {
  ColorPrinterOptionItem,
  ColorProfileDownloadLinkResult,
  ColorProfileItem,
  getColorConfigOptions,
  getColorProfileDownloadLink,
  getColorProfileList,
  getColorProfileZipDownloadLink,
} from '../../api/color/index';

const WECHAT_JSSDK_URL = 'https://res.wx.qq.com/open/js/jweixin-1.6.0.js';
let wechatSdkPromise: Promise<void> | null = null;

export default Vue.extend({
  name: 'ColorPage',
  data() {
    return {
      fileLoading: false,
      batchDownloadLoading: false,
      isMobile: false,
      isWechatBrowser: false,
      isMiniProgramEnv: false,
      searchKeyword: '',
      printerTree: [] as ColorPrinterOptionItem[],
      printerOptions: [] as string[],
      mediaOptions: [] as string[],
      fileList: [] as ColorProfileItem[],
      userFilters: {
        printerModel: '',
        mediaName: '',
      },
    };
  },
  computed: {
    emptyText(): string {
      if (!this.userFilters.printerModel || !this.userFilters.mediaName) {
        return '请先选择打印机型号和介质类型';
      }
      if (this.searchKeyword) {
        return '当前筛选条件下没有匹配的文件';
      }
      return '当前组合下暂无已发布文件';
    },
  },
  async mounted() {
    this.checkScreenSize();
    window.addEventListener('resize', this.checkScreenSize);
    await this.detectWechatEnv();
    await this.fetchOptions();
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkScreenSize);
  },
  methods: {
    checkScreenSize() {
      this.isMobile = window.innerWidth <= 768;
    },
    async ensureWechatSdk() {
      const currentWindow = window as any;
      if (currentWindow.wx && currentWindow.wx.miniProgram) {
        return;
      }
      if (!wechatSdkPromise) {
        wechatSdkPromise = new Promise<void>((resolve, reject) => {
          const existingScript = document.querySelector(`script[src="${WECHAT_JSSDK_URL}"]`) as HTMLScriptElement | null;
          if (existingScript) {
            if ((window as any).wx) {
              resolve();
              return;
            }
            existingScript.addEventListener('load', () => resolve(), { once: true });
            existingScript.addEventListener('error', () => reject(new Error('微信JSSDK加载失败')), { once: true });
            return;
          }
          const script = document.createElement('script');
          script.src = WECHAT_JSSDK_URL;
          script.async = true;
          script.onload = () => resolve();
          script.onerror = () => reject(new Error('微信JSSDK加载失败'));
          document.head.appendChild(script);
        }).catch(error => {
          wechatSdkPromise = null;
          throw error;
        });
      }
      await wechatSdkPromise;
    },
    async detectWechatEnv() {
      const currentWindow = window as any;
      const ua = window.navigator.userAgent.toLowerCase();
      this.isWechatBrowser = ua.indexOf('micromessenger') > -1;
      if (currentWindow.__wxjs_environment === 'miniprogram') {
        this.isMiniProgramEnv = true;
        return;
      }
      if (!this.isWechatBrowser && ua.indexOf('miniprogram') === -1) {
        this.isMiniProgramEnv = false;
        return;
      }
      try {
        await this.ensureWechatSdk();
        if (currentWindow.wx && currentWindow.wx.miniProgram && typeof currentWindow.wx.miniProgram.getEnv === 'function') {
          this.isMiniProgramEnv = await new Promise(resolve => {
            currentWindow.wx.miniProgram.getEnv((res: { miniprogram?: boolean }) => {
              resolve(!!(res && res.miniprogram));
            });
          });
          return;
        }
      } catch (error) {
        // Ignore SDK detection failure and fall back to UA-based detection.
      }
      this.isMiniProgramEnv = ua.indexOf('miniprogram') > -1;
    },
    getAuthToken() {
      return AccountModule.token || Cache.getToken('user') || '';
    },
    resolveDownloadUrl(url: string) {
      if (/^https?:\/\//i.test(url)) {
        return url;
      }
      return new URL(url, window.location.origin).toString();
    },
    async postMiniProgramDownloadMessage(payload: Record<string, any>) {
      await this.ensureWechatSdk();
      const currentWindow = window as any;
      if (!currentWindow.wx || !currentWindow.wx.miniProgram || typeof currentWindow.wx.miniProgram.navigateTo !== 'function') {
        throw new Error('小程序下载通道不可用');
      }
      const encodedPayload = encodeURIComponent(JSON.stringify(payload));
      currentWindow.wx.miniProgram.navigateTo({
        url: `/pages/help/downloadfile?payload=${encodedPayload}`,
      });
    },
    async executeDownload(meta: ColorProfileDownloadLinkResult, extraPayload: Record<string, any> = {}) {
      const token = this.getAuthToken();
      if (this.isMiniProgramEnv) {
        await this.postMiniProgramDownloadMessage({
          type: 'download',
          downloadType: meta.downloadType,
          url: this.resolveDownloadUrl(meta.downloadUrl),
          token,
          fileName: meta.fileName,
          ...extraPayload,
        });
        this.$message.success('已发送下载请求，请在小程序内查看');
        return;
      }
      await downloadByAuthUrl(meta.downloadUrl, token, meta.fileName);
    },
    showDownloadEnvMessage() {
      this.$alert('当前环境不支持直接下载，请点击右上角“···”，选择“在浏览器中打开”后再下载。', '下载提示', {
        confirmButtonText: '我知道了',
      });
    },
    async fetchOptions() {
      const res = await getColorConfigOptions();
      const printerTree = Array.isArray(res && res.printerTree) ? (res!.printerTree as ColorPrinterOptionItem[]) : [];
      this.printerTree = printerTree;
      this.printerOptions = this.printerTree.map(item => item.printerModel);
      if (!this.printerOptions.length) {
        this.printerOptions = Array.isArray(res && res.printerModelList) ? res!.printerModelList : [];
      }
      this.syncMediaOptions();
    },
    handlePrinterChange() {
      this.userFilters.mediaName = '';
      this.fileList = [];
      this.syncMediaOptions();
    },
    handleMediaChange() {
      this.fileList = [];
    },
    async handleSearch() {
      if (!this.userFilters.printerModel || !this.userFilters.mediaName) {
        this.fileList = [];
        return;
      }
      this.fileLoading = true;
      try {
        const res = await getColorProfileList({
          printerModel: this.userFilters.printerModel,
          mediaName: this.userFilters.mediaName,
          fileName: this.searchKeyword || undefined,
        });
        this.fileList = Array.isArray(res) ? res : [];
      } catch (error) {
        this.fileList = [];
      } finally {
        this.fileLoading = false;
      }
    },
    async handleDownload(item: ColorProfileItem) {
      try {
        const link = await getColorProfileDownloadLink({ sn: item.sn });
        if (!link || !link.downloadUrl) {
          throw new Error('下载链接获取失败');
        }
        await this.executeDownload(link, {
          sn: item.sn,
        });
      } catch (error) {
        const message = (error && (error as Error).message) || '下载失败';
        if (this.isWechatBrowser && !this.isMiniProgramEnv && (message === '下载失败' || message === '网络异常')) {
          this.showDownloadEnvMessage();
          return;
        }
        this.$message.error(message);
      }
    },
    async handleDownloadAll() {
      if (!this.userFilters.printerModel || !this.userFilters.mediaName) {
        this.$message.warning('请先选择打印机和介质类型');
        return;
      }
      if (!this.fileList.length) {
        this.$message.warning('当前条件下暂无可下载文件');
        return;
      }
      this.batchDownloadLoading = true;
      try {
        const link = await getColorProfileZipDownloadLink({
          printerModel: this.userFilters.printerModel,
          mediaName: this.userFilters.mediaName,
          fileName: this.searchKeyword || undefined,
        });
        if (!link || !link.downloadUrl) {
          throw new Error('下载链接获取失败');
        }
        await this.executeDownload(link, {
          printerModel: this.userFilters.printerModel,
          mediaName: this.userFilters.mediaName,
        });
      } catch (error) {
        const message = (error && (error as Error).message) || '下载失败';
        if (this.isWechatBrowser && !this.isMiniProgramEnv && (message === '下载失败' || message === '网络异常')) {
          this.showDownloadEnvMessage();
          return;
        }
        this.$message.error(message);
      } finally {
        this.batchDownloadLoading = false;
      }
    },
    getFileTypeClass(fileType: string) {
      return `file-type-${String(fileType || '').toLowerCase()}`;
    },
    syncMediaOptions() {
      if (!this.userFilters.printerModel) {
        this.mediaOptions = [];
        return;
      }
      const printerItem = this.printerTree.find(item => item.printerModel === this.userFilters.printerModel);
      this.mediaOptions = printerItem && Array.isArray(printerItem.mediaList) ? printerItem.mediaList.map(item => item.mediaName) : [];
      if (this.userFilters.mediaName && !this.mediaOptions.includes(this.userFilters.mediaName)) {
        this.userFilters.mediaName = '';
      }
    },
    toBuy(type: string) {
      this.$router.push(`/typePay?type=${type}`);
    },
  },
});
